import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Minus, Plus, ArrowLeft } from 'lucide-react'
import { api } from '@/lib/api'
import { Seo } from '@/components/Seo'
import ReviewList from '@/components/ReviewList'
import { formatPrice, toNumber } from '@/lib/money'
import { useCartStore } from '@/lib/cart'
import { Skeleton } from '@/components/ui/skeleton'

export default function ProductDetail() {
  const { id = '' } = useParams()
  const [quantity, setQuantity] = useState(1)
  const addItem = useCartStore((s) => s.addItem)

  const product = useQuery({
    queryKey: ['product', id],
    queryFn: () => api.products.get(id),
    enabled: Boolean(id),
  })

  if (product.isLoading) {
    return (
      <div data-upbo-src="src/pages/ProductDetail.tsx:25" className="mx-auto grid w-full max-w-6xl gap-12 px-6 py-16 lg:grid-cols-2">
        <Skeleton className="aspect-square rounded-2xl" />
        <div data-upbo-src="src/pages/ProductDetail.tsx:27" className="space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    )
  }

  if (product.isError || !product.data) {
    return (
      <div data-upbo-src="src/pages/ProductDetail.tsx:38" className="mx-auto w-full max-w-6xl px-6 py-24 text-center">
        <p data-upbo-src="src/pages/ProductDetail.tsx:39" className="text-[#9c7a72]">Không tìm thấy sản phẩm này.</p>
        <Link to="/cua-hang" className="mt-4 inline-block text-sm text-[#7a4f45] underline">
          Quay lại cửa hàng
        </Link>
      </div>
    )
  }

  const item = product.data
  const compareAt = toNumber(item.compareAtPrice)
  const price = toNumber(item.price)

  return (
    <>
      <Seo title={`${item.name} — LumiSkin`} description={item.description} />

      <div data-upbo-src="src/pages/ProductDetail.tsx:55" className="mx-auto w-full max-w-6xl px-6 py-12">
        <Link to="/cua-hang" className="inline-flex items-center gap-2 text-sm text-[#b3897f] hover:text-[#7a4f45]">
          <ArrowLeft className="h-4 w-4" /> Cửa hàng
        </Link>

        <div data-upbo-src="src/pages/ProductDetail.tsx:60" className="mt-8 grid gap-12 lg:grid-cols-2">
          <div data-upbo-src="src/pages/ProductDetail.tsx:61" className="overflow-hidden rounded-2xl bg-[#f7ebe7]">
            {item.image ? (
              <img data-upbo-src="src/pages/ProductDetail.tsx:63" src={item.image} alt={item.name} className="aspect-square w-full object-cover" />
            ) : (
              <div data-upbo-src="src/pages/ProductDetail.tsx:65" className="flex aspect-square items-center justify-center text-sm text-[#c0a49d]">
                {item.name}
              </div>
            )}
          </div>

          <div data-upbo-src="src/pages/ProductDetail.tsx:71">
            <h1 data-upbo-src="src/pages/ProductDetail.tsx:72" className="text-4xl font-semibold tracking-[-0.02em] text-[#5f3f38]">{item.name}</h1>

            <div data-upbo-src="src/pages/ProductDetail.tsx:74" className="mt-4 flex items-baseline gap-3">
              <span data-upbo-src="src/pages/ProductDetail.tsx:75" className="text-2xl font-semibold text-[#7a4f45]">{formatPrice(item.price)}</span>
              {compareAt > price && (
                <span data-upbo-src="src/pages/ProductDetail.tsx:77" className="text-sm text-[#c0a49d] line-through">{formatPrice(item.compareAtPrice)}</span>
              )}
            </div>

            <p data-upbo-src="src/pages/ProductDetail.tsx:81" className="mt-6 leading-relaxed text-[#9c7a72]">{item.description}</p>

            <div data-upbo-src="src/pages/ProductDetail.tsx:83" className="mt-8 flex items-center gap-4">
              <div data-upbo-src="src/pages/ProductDetail.tsx:84" className="flex items-center rounded-full border border-[#e0c8c1]">
                <button data-upbo-src="src/pages/ProductDetail.tsx:85"
                  type="button"
                  aria-label="Giảm số lượng"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="flex h-10 w-10 items-center justify-center text-[#7a4f45]"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span data-upbo-src="src/pages/ProductDetail.tsx:93" className="w-10 text-center text-sm text-[#5f3f38]">{quantity}</span>
                <button data-upbo-src="src/pages/ProductDetail.tsx:94"
                  type="button"
                  aria-label="Tăng số lượng"
                  onClick={() => setQuantity((q) => q + 1)}
                  className="flex h-10 w-10 items-center justify-center text-[#7a4f45]"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              <button data-upbo-src="src/pages/ProductDetail.tsx:104"
                type="button"
                onClick={() => addItem(item, quantity)}
                className="rounded-full bg-[#7a4f45] px-8 py-3 text-sm font-medium text-white transition-colors hover:bg-[#63403a]"
              >
                Thêm vào giỏ
              </button>
            </div>

            {item.stock <= 0 && (
              <p data-upbo-src="src/pages/ProductDetail.tsx:114" className="mt-4 text-sm text-[#c98b7e]">Sản phẩm đang tạm hết hàng.</p>
            )}
          </div>
        </div>

        <div data-upbo-src="src/pages/ProductDetail.tsx:119" className="mt-20 border-t border-[#eedfda] pt-12">
          <h2 data-upbo-src="src/pages/ProductDetail.tsx:120" className="text-2xl font-semibold tracking-[-0.02em] text-[#5f3f38]">Đánh giá của khách</h2>
          <div data-upbo-src="src/pages/ProductDetail.tsx:121" className="mt-6">
            <ReviewList productId={item.id} />
          </div>
        </div>
      </div>
    </>
  )
}
